const state = () => ({
  // --- 기존 스트리밍 상태 ---
  chunks: {},
  activeStreamingConversations: new Set(),
  finalizedConversations: new Set(),
  reasoningChunks: {},
  contentAccumulator: {},
  reasoningAccumulator: {},

  // --- 시간 측정 기능 추가 ---
  timeInfo: {},
  processingStartTime: {},
  uiStartTime: {},
  uiEndTime: {},
  retrievalInfo: {},
  timer: null,
  currentTime: null,
});

const getters = {
  // --- 기존 스트리밍 Getters ---
  getStreamingChunks: streamingState => conversationId => {
    const chunks = streamingState.chunks[conversationId] || [];
    return [...chunks].sort((a, b) => a.sequenceId - b.sequenceId);
  },
  getReasoningChunks: streamingState => conversationId => {
    const chunks = streamingState.reasoningChunks[conversationId] || [];
    return [...chunks].sort((a, b) => a.sequenceId - b.sequenceId);
  },
  getAccumulatedLength: streamingState => conversationId => {
    return streamingState.contentAccumulator[conversationId] || 0;
  },
  getReasoningAccumulatedLength: streamingState => conversationId => {
    return streamingState.reasoningAccumulator[conversationId] || 0;
  },
  hasStreamingMessages: streamingState => conversationId => {
    return streamingState.chunks[conversationId]?.length > 0;
  },
  isConversationStreaming: streamingState => conversationId => {
    return streamingState.activeStreamingConversations.has(conversationId);
  },

  // --- 시간 측정 Getters 추가 ---
  getTimeInfo: streamingState => conversationId => {
    return streamingState.timeInfo[conversationId] || {
      rag_time: 0, reasoning_time: 0, content_time: 0, total_time: 0,
    };
  },
  getCurrentProcessingTime: streamingState => conversationId => {
    const startTime = streamingState.processingStartTime[conversationId];
    if (!startTime || !streamingState.currentTime) return 0;
    return (streamingState.currentTime - startTime) / 1000;
  },
  getClientTotalTime: streamingState => conversationId => {
    const startTime = streamingState.uiStartTime[conversationId];
    const endTime = streamingState.uiEndTime[conversationId];
    if (!startTime) return 0;
    const endTimeToUse = endTime || streamingState.currentTime || Date.now();
    return (endTimeToUse - startTime) / 1000;
  },
  getRetrievalInfo: streamingState => conversationId => {
    return streamingState.retrievalInfo[conversationId] || null;
  },
};

const actions = {
  addChunk({ commit, state: streamingState, dispatch }, chunkData) {
    const conversationId = parseInt(chunkData.conversation_id, 10);

    if (streamingState.finalizedConversations.has(conversationId)) {
      commit('RESET_CONVERSATION_FOR_NEW_STREAMING', conversationId);
      // 새 스트림 시작 시, 가장 먼저 UI 타이머 시작점을 기록 (핵심 수정)
      commit('SET_UI_TIMING', { conversationId, startTime: Date.now(), endTime: null });
    }

    const chunk = {
      id: chunkData.id || `${Date.now()}-${chunkData.sequence_id}`,
      conversationId,
      content: chunkData.content || chunkData.chunk_content || '',
      type: chunkData.type || chunkData.chunk_type || 'content',
      timestamp: new Date().getTime(),
      sequenceId: chunkData.sequence_id || chunkData.chunk_info?.chunk_number,
      messageId: chunkData.message_id,
      isComplete: chunkData.is_complete || false,
      cotMetadata: chunkData.cot_metadata || {},
      chunkInfo: chunkData.chunk_info || {},
    };

    if (typeof chunk.sequenceId !== 'number') return;

    const isStreaming = !chunk.isComplete;
    const wasStreaming = streamingState.activeStreamingConversations.has(conversationId);

    // 스트리밍 시작/종료 시 타이머 관리
    if (isStreaming && !wasStreaming) {
      if (!streamingState.uiStartTime[conversationId]) {
        commit('SET_UI_TIMING', { conversationId, startTime: Date.now(), endTime: null });
      }
      dispatch('startRealtimeTimer');
      commit('START_PROCESSING_TIMER', conversationId);
    } else if (!isStreaming && wasStreaming) {
      dispatch('stopRealtimeTimer');
      commit('SET_UI_TIMING', { conversationId, endTime: Date.now() });
    }

    // 청크 타입에 따른 처리
    commit('UPDATE_RETRIEVAL_INFO', chunk);
    if (chunk.type === 'reasoning') {
      commit('ADD_REASONING_CHUNK', chunk);
    } else {
      commit('ADD_CONTENT_CHUNK', chunk);
    }

    commit('SET_CONVERSATION_STREAMING', { conversationId, isStreaming });

    // 스트리밍 완료 시 최종 처리
    if (chunk.isComplete) {
      commit('COMPLETE_STREAMING', conversationId);
      if (chunk.messageId) {
        dispatch('saveFinalTimeInfo', { conversationId, messageId: chunk.messageId });
      }
    }
  },

  clearStreamingMessages({ commit, dispatch }, conversationId) {
    const id = parseInt(conversationId, 10);
    commit('CLEAR_CONVERSATION_CHUNKS', id);
    dispatch('stopRealtimeTimer');
  },

  completeStreaming({ commit, dispatch }, conversationId) {
    const id = parseInt(conversationId, 10);
    commit('COMPLETE_STREAMING', id);
    commit('SET_UI_TIMING', { conversationId, endTime: Date.now() });
    dispatch('stopRealtimeTimer');
  },

  resetConversation({ commit }, conversationId) {
    const id = parseInt(conversationId, 10);
    commit('RESET_CONVERSATION_FOR_NEW_STREAMING', id);
  },

  // --- 시간 측정 Actions 추가 ---
  updateTimeInfo({ commit }, { conversationId, timeInfo, eventType }) {
    commit('UPDATE_TIME_INFO', { conversationId, timeInfo, eventType });
  },
  startRealtimeTimer({ commit, state: streamingState }) {
    if (streamingState.timer) return;
    const timer = setInterval(() => commit('UPDATE_CURRENT_TIME'), 100);
    commit('SET_TIMER', timer);
  },
  stopRealtimeTimer({ commit, state: streamingState }) {
    if (streamingState.timer) {
      clearInterval(streamingState.timer);
      commit('SET_TIMER', null);
    }
    commit('UPDATE_CURRENT_TIME');
  },
  async saveFinalTimeInfo({ state: streamingState }, { conversationId, messageId }) {
    const timeInfo = streamingState.timeInfo[conversationId];
    const clientStartTime = streamingState.uiStartTime[conversationId];
    const clientEndTime = streamingState.uiEndTime[conversationId] || Date.now();

    if (!timeInfo || !messageId) {
      return;
    }

    // Calculate client time
    const clientTime = clientStartTime ? (clientEndTime - clientStartTime) / 1000 : 0;

    // Prepare time data to save
    const timeData = { ...timeInfo };
    if (clientTime > 0) {
      timeData.client_time = clientTime;
    }

    try {
      const requestBody = { time_info: timeData };

      const response = await fetch(`/api/v1/widget/messages/${messageId}/update_time_info${window.location.search}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': window.authToken
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to save time_info:', response.statusText, errorText);
      } else {
        const responseData = await response.json();
      }
    } catch (error) {
      console.error('Error saving time_info:', error);
    }
  },
};

const mutations = {
  ADD_REASONING_CHUNK(streamingState, chunk) {
    const { conversationId, cotMetadata } = chunk;
    if (streamingState.finalizedConversations.has(conversationId)) return;
    if (!streamingState.reasoningChunks[conversationId]) {
      streamingState.reasoningChunks[conversationId] = [];
    }
    streamingState.reasoningChunks[conversationId].push(chunk);
    if (cotMetadata && cotMetadata.accumulated_length) {
      streamingState.reasoningAccumulator[conversationId] = cotMetadata.accumulated_length;
    }
  },

  ADD_CONTENT_CHUNK(streamingState, chunk) {
    const { conversationId, cotMetadata } = chunk;
    if (streamingState.finalizedConversations.has(conversationId)) return;
    if (!streamingState.chunks[conversationId]) {
      streamingState.chunks[conversationId] = [];
    }
    streamingState.chunks[conversationId].push(chunk);
    if (cotMetadata && cotMetadata.accumulated_length) {
      streamingState.contentAccumulator[conversationId] = cotMetadata.accumulated_length;
    }
  },

  SET_CONVERSATION_STREAMING(streamingState, { conversationId, isStreaming }) {
    if (isStreaming) {
      streamingState.activeStreamingConversations.add(conversationId);
    } else {
      streamingState.activeStreamingConversations.delete(conversationId);
    }
  },

  CLEAR_CONVERSATION_CHUNKS(streamingState, conversationId) {
    streamingState.finalizedConversations.add(conversationId);
    if (streamingState.chunks[conversationId]) streamingState.chunks[conversationId] = [];
    if (streamingState.reasoningChunks[conversationId]) streamingState.reasoningChunks[conversationId] = [];
    if (streamingState.contentAccumulator[conversationId]) delete streamingState.contentAccumulator[conversationId];
    if (streamingState.reasoningAccumulator[conversationId]) delete streamingState.reasoningAccumulator[conversationId];

    // 시간 관련 상태 초기화 추가
    if (streamingState.timeInfo[conversationId]) delete streamingState.timeInfo[conversationId];
    if (streamingState.processingStartTime[conversationId]) delete streamingState.processingStartTime[conversationId];
    if (streamingState.uiStartTime[conversationId]) delete streamingState.uiStartTime[conversationId];
    if (streamingState.uiEndTime[conversationId]) delete streamingState.uiEndTime[conversationId];
    if (streamingState.retrievalInfo[conversationId]) delete streamingState.retrievalInfo[conversationId];

    streamingState.activeStreamingConversations.delete(conversationId);
  },

  COMPLETE_STREAMING(streamingState, conversationId) {
    streamingState.activeStreamingConversations.delete(conversationId);
  },

  RESET_CONVERSATION_FOR_NEW_STREAMING(streamingState, conversationId) {
    streamingState.finalizedConversations.delete(conversationId);
    if (streamingState.chunks[conversationId]) streamingState.chunks[conversationId] = [];
    if (streamingState.reasoningChunks[conversationId]) streamingState.reasoningChunks[conversationId] = [];
    if (streamingState.contentAccumulator[conversationId]) delete streamingState.contentAccumulator[conversationId];
    if (streamingState.reasoningAccumulator[conversationId]) delete streamingState.reasoningAccumulator[conversationId];

    // 시간 관련 상태 초기화 추가
    if (streamingState.timeInfo[conversationId]) delete streamingState.timeInfo[conversationId];
    if (streamingState.processingStartTime[conversationId]) delete streamingState.processingStartTime[conversationId];
    if (streamingState.uiStartTime[conversationId]) delete streamingState.uiStartTime[conversationId];
    if (streamingState.uiEndTime[conversationId]) delete streamingState.uiEndTime[conversationId];
    if (streamingState.retrievalInfo[conversationId]) delete streamingState.retrievalInfo[conversationId];

    streamingState.activeStreamingConversations.delete(conversationId);
  },

  // --- 시간 측정 Mutations 추가 ---
  UPDATE_TIME_INFO(streamingState, { conversationId, timeInfo, eventType }) {
    if (!streamingState.timeInfo[conversationId]) {
      streamingState.timeInfo[conversationId] = {};
    }
    Object.assign(streamingState.timeInfo[conversationId], timeInfo);
    if (!streamingState.processingStartTime[conversationId] && (eventType === 'content' || eventType === 'stream_start')) {
      streamingState.processingStartTime[conversationId] = Date.now();
    }
  },
  START_PROCESSING_TIMER(streamingState, conversationId) {
    streamingState.processingStartTime[conversationId] = Date.now();
    streamingState.currentTime = Date.now();
  },
  SET_UI_TIMING(streamingState, { conversationId, startTime, endTime }) {
    if (startTime) streamingState.uiStartTime[conversationId] = startTime;
    if (endTime) streamingState.uiEndTime[conversationId] = endTime;
  },
  SET_TIMER(streamingState, timer) {
    streamingState.timer = timer;
  },
  UPDATE_CURRENT_TIME(streamingState) {
    streamingState.currentTime = Date.now();
  },
  UPDATE_RETRIEVAL_INFO(streamingState, chunk) {
    const { conversationId, cotMetadata } = chunk;
    if (cotMetadata && cotMetadata.description) {
      streamingState.retrievalInfo[conversationId] = {
        description: cotMetadata.description,
        stage: cotMetadata.stage || 'processing',
        type: cotMetadata.type || 'unknown',
        timestamp: Date.now(),
      };
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
